import { Component, ElementRef, HostListener, Inject, OnInit, PLATFORM_ID } from '@angular/core';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { CustomerAdminService } from '../../../_services/api/customer-admin.service';
import { ApiResponse } from '../../../_models/api-response.model';
import { UserProfileAdmin } from '../../../_models/user.model';
import { NotificationService } from '../../../_services/app/notification.service';
import { MenuStateService } from '../../../_services/app/menu-state.service';
import { CustomerDetail } from '../../../_models/customer.model';
import { AdminMemberReservationsCard } from '../admin-member-reservations-card/admin-member-reservations-card';

@Component({
  selector: 'app-admin-member-details',
  standalone: true,
  imports: [CommonModule, FormsModule, AdminMemberReservationsCard],
  templateUrl: './admin-member-details.html',
  styleUrls: ['./admin-member-details.scss']
})
export class AdminMemberDetails implements OnInit {
  memberId!: number;
  member: CustomerDetail | null = null;
  isLoading = false;
  errorMessage: string | null = null;

  menuOpen = false;
  showDeleteModal = false;
  isUpdatingVisibility = false;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private customerService: CustomerAdminService,
    private notification: NotificationService,
    private elementRef: ElementRef, 
    private menuStateService: MenuStateService,
    @Inject(PLATFORM_ID) private platformId: Object
  ) {}

  ngOnInit(): void {    
    this.loadMemberId();
    if(this.memberId){
      this.menuStateService.activeMenu$.subscribe(activeId => {
      this.menuOpen = activeId === this.memberId;
    });
    if (isPlatformBrowser(this.platformId)) {
        document.addEventListener('click', this.closeMenu.bind(this));
    }
    }
  }

  ngOnDestroy() {
    if (isPlatformBrowser(this.platformId)) {
        document.removeEventListener('click', this.closeMenu.bind(this));
    }
  }

  toggleMenu(event: MouseEvent) {
    event.stopPropagation();
    if (this.menuOpen) {
      this.menuStateService.setActiveMenu(null);
    } else {
      this.menuStateService.setActiveMenu(this.memberId);
    }
  }

  @HostListener('document:click', ['$event'])
  closeMenu(event: Event) {
    if (!this.elementRef.nativeElement.contains(event.target)) {
      this.menuOpen = false;
    }
    if (this.menuOpen) {
      this.menuStateService.setActiveMenu(null);
    }
  }

  setDefaultImage(event: any): void {
    event.target.src = './assets/images/user-default.png';
  }

  private loadMemberId(): void {
    this.route.paramMap.subscribe(params => {
      const id = params.get('id');
      
      if (id) {
        this.memberId = +id;
        this.loadMemberDetails();
      } else {
        this.errorMessage = 'ID du client non trouvé';
        this.goToList();
      }
    });
  }

  private loadMemberDetails(): void {
    this.isLoading = true;
    this.errorMessage = null;

    this.customerService.getCustomerDetails(this.memberId).subscribe({
      next: (response: ApiResponse<CustomerDetail>) => {
        if (response.success && response.data) {
          this.member = response.data;
          console.log(this.member);
        } else {
          this.errorMessage = response.message || 'Erreur lors du chargement des données';
        }
        this.isLoading = false;
      },
      error: (error) => {
        console.error('Error loading client details:', error);
        this.errorMessage = 'Erreur de chargement des détails du client';
        this.isLoading = false;
        if (error.status === 404) {
          this.errorMessage = 'Client non trouvé';
          setTimeout(() => this.goToList(), 2000);
        }
      }
    });
  }

  goToList(): void {
    this.router.navigate(['/admin/clients']);
  }

  edit(){
    if (this.member) {
      this.router.navigate(['/admin/clients/edit', this.member.id]);
    }
  }

  formatAddress(address: any): string {
    if (!address) return 'Non spécifiée';
    
    const parts: string[] = [];
    
    if (address.streetNumber && address.street) {
      parts.push(`${address.street} ${address.streetNumber}`);
    } else if (address.street) {
      parts.push(address.street);
    }
    
    if (address.postalCode && address.city) {
      parts.push(`${address.postalCode} ${address.city}`);
    } else if (address.postalCode) {
      parts.push(address.postalCode);
    } else if (address.city) {
      parts.push(address.city);
    }
    
    if (parts.length === 0) {
      return 'Non spécifiée';
    }
    
    return parts.join(', ') + ".";
  }

  formatDate(dateString: string): string {
    if (!dateString) return '';
    
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-BE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  }

  getStatusClass(): string {
    if (!this.member) return '';
    
    // Check if user has roles to determine status
    return this.member.roles && this.member.roles.length > 0 ? 'active' : 'inactive';
  }

  toggleVisibility(): void {
    if (!this.member) return;
    if (this.isUpdatingVisibility) return;
    
    this.isUpdatingVisibility = true;
    this.menuStateService.setActiveMenu(null);
    this.menuOpen = false;
    
    if (this.member.isEnabled) {
      this.customerService.disableCustomer(this.member.id).subscribe({
        next: () => {
          this.member!.isEnabled = false;
          this.isUpdatingVisibility = false;
          this.notification.showSuccess("Le client est maintenant désactivé et n’a plus accès à la plateforme.");
        },
        error: (error) => {
          console.error('Error making client disabled:', error);
          this.isUpdatingVisibility = false;
          this.notification.showError('Erreur lors de la modification de la visibilité');
        }
      });
    } else {
      this.customerService.enableCustomer(this.member.id).subscribe({
        next: () => {
          this.member!.isEnabled = true;
          this.isUpdatingVisibility = false;
          this.notification.showSuccess("Le client est maintenant actif et peut de nouveau accéder à la plateforme.");
        },
        error: (error) => {
          console.error('Error making client enabled:', error);
          this.isUpdatingVisibility = false;
          this.notification.showError('Erreur lors de la modification de la visibilité');
        }
      });
    }
  }

  editMember(): void {
    if (this.member) {
      this.router.navigate(['/admin/clients/edit', this.memberId]);
    }
  }

  confirmDelete(): void {
    this.menuOpen = false;
    this.showDeleteModal = true;
  }

  closeDeleteModal(): void {
    this.showDeleteModal = false;
  }

  deleteMember(): void {
    if(!this.member){return}
    this.customerService.deleteCustomer(this.member.id).subscribe({
      next: () => {
        this.showDeleteModal = false;
        this.goToList();
      },
      error: (error) => {
        console.error('Error deleting membre:', error);
        this.notification.showError('Erreur lors de la suppression du member');
        this.showDeleteModal = false;
      }
    });
  }
}